import React, { useCallback, useEffect, useState } from "react";
import { CopyCheck, Loader2, ShieldCheck, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../../components/layout/AdminShell";
import EmptyState from "../../components/EmptyState";
import ReceiptDetailDrawer from "../../components/ReceiptDetailDrawer";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n";
import { DuplicatesApi, ReceiptsApi, errorMessage } from "../../lib/api";
import { formatDate, formatDateTime, formatMoney } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";

const ReceiptCard = ({ receipt, label, currency, onOpen, testId }) => {
  const { t } = useI18n();
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    let url;
    let cancelled = false;
    if (receipt?.id) {
      ReceiptsApi.image(receipt.id)
        .then((blob) => {
          if (cancelled) return;
          url = URL.createObjectURL(blob);
          setImageUrl(url);
        })
        .catch(() => setImageUrl(null));
    }
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [receipt?.id]);

  if (!receipt) return null;
  return (
    <div className="rounded-xl border bg-card p-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        <StatusBadge status={receipt.status} />
      </div>
      <button
        type="button"
        onClick={() => onOpen(receipt.id)}
        className="mt-2 block w-full overflow-hidden rounded-lg border bg-muted/40"
        aria-label={receipt.vendor_name || "receipt"}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={receipt.vendor_name || "receipt"}
            className="max-h-[200px] w-full object-contain"
          />
        ) : (
          <div className="flex h-[120px] items-center justify-center text-xs text-muted-foreground">
            {t("drawer.loadingImage")}
          </div>
        )}
      </button>
      <dl className="mt-2 space-y-1 text-sm">
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t("common.vendor")}</dt>
          <dd className="min-w-0 truncate font-medium">{receipt.vendor_name || "—"}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t("common.receiptNo")}</dt>
          <dd className="font-mono text-xs">{receipt.receipt_number || "—"}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t("common.date")}</dt>
          <dd>{receipt.issue_date ? formatDate(receipt.issue_date) : "—"}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t("common.total")}</dt>
          <dd className="ll-num font-semibold">
            {formatMoney(receipt.gross_total, receipt.currency || currency)}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t("duplicates.uploadedBy")}</dt>
          <dd className="min-w-0 truncate">
            {receipt.user_name} · {formatDateTime(receipt.created_at)}
          </dd>
        </div>
      </dl>
    </div>
  );
};

const Duplicates = () => {
  const { currency, categories } = useAuth();
  const { t } = useI18n();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await DuplicatesApi.list();
      setGroups(data.groups || []);
    } catch (e) {
      toast.error(errorMessage(e, "Could not load duplicates"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action) => {
    setBusy(`${id}-${action}`);
    try {
      if (action === "keep") {
        await DuplicatesApi.keep(id);
        toast.success(t("duplicates.keptToast"));
      } else if (action === "confirm") {
        await DuplicatesApi.confirm(id);
        toast.success(t("duplicates.confirmedToast"));
      } else {
        await DuplicatesApi.remove(id);
        toast.success(t("duplicates.deletedToast"));
      }
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Action failed"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <AdminShell title={t("duplicates.title")} subtitle={t("duplicates.sub")}>
      {loading && !groups.length ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title={t("duplicates.emptyTitle")}
          description={t("duplicates.emptyBody")}
          testId="duplicates-empty"
        />
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.receipt.id} className="ll-card p-4" data-testid="duplicate-group">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <CopyCheck className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-medium">
                    {g.reason === "identical_image"
                      ? t("duplicates.reasonImage")
                      : t("duplicates.reasonSame")}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy === `${g.receipt.id}-keep`}
                    onClick={() => act(g.receipt.id, "keep")}
                    data-testid="duplicate-keep-button"
                  >
                    {busy === `${g.receipt.id}-keep` ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ShieldCheck className="mr-2 h-4 w-4" />
                    )}
                    {t("duplicates.keepBoth")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy === `${g.receipt.id}-confirm`}
                    onClick={() => act(g.receipt.id, "confirm")}
                    data-testid="duplicate-confirm-button"
                  >
                    <XCircle className="mr-2 h-4 w-4" /> {t("duplicates.confirmDup")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    disabled={busy === `${g.receipt.id}-delete`}
                    onClick={() => act(g.receipt.id, "delete")}
                    data-testid="duplicate-delete-button"
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> {t("duplicates.deleteScan")}
                  </Button>
                </div>
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <ReceiptCard
                  receipt={g.receipt}
                  label={t("duplicates.newReceipt")}
                  currency={currency}
                  onOpen={setOpenId}
                  testId="duplicate-new-card"
                />
                <ReceiptCard
                  receipt={g.original}
                  label={t("duplicates.originalReceipt")}
                  currency={currency}
                  onOpen={setOpenId}
                  testId="duplicate-original-card"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <ReceiptDetailDrawer
        receiptId={openId}
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        onChanged={load}
        isAdmin
        categories={categories}
        currency={currency}
      />
    </AdminShell>
  );
};

export default Duplicates;
